/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication and/or specific roles.
 * Automatically redirects to login if user is not authenticated.
 * Loads user data directly from Firestore if Firebase user exists but app state is empty.
 */

import React, { useEffect, useState } from 'react'
import { Navigate, useNavigate, useLocation } from 'react-router-dom'
import { getAuth } from 'firebase/auth'
import useAppState from '../hooks/useAppState'
import { getUser } from '../libs/DBApiHandlerDeprecated'
import { setSentryUser } from '../libs/SentryHelper'
import { getEmailForFirebaseUser } from '../../../pure-js/libs/Common'
import { LoadingPage } from './LoadingPage'
import { NotAuthorisedPage } from './NotAuthorisedPage'
import { getRouteMetadata, RouteType } from '../config/routes/routeConfig'
import { redirectUnauthorizedUser } from '../utils/routeProtection'
import { getActiveOrgRole } from '../features/organization/orgStateHelpers'
import { isPlatformSuperAdmin } from '../../../pure-js/libs/isSuperUser'
import { OrgRole } from '../../../pure-js/types/OrganizationMembership'

interface ProtectedRouteProps {
  children: React.ReactNode
  allowedRoles?: OrgRole[]
  routeType?: RouteType
  fallbackPath?: string
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  allowedRoles,
  routeType,
  fallbackPath = '/login'
}) => {
  const { state, setState } = useAppState()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLoadingUser, setIsLoadingUser] = useState(false)

  useEffect(() => {
    const loadUserData = async () => {
      // Wait for auth to initialize before making any decisions
      if (!state.isAuthInitialized) {
        return
      }

      // If we already have user data, skip loading
      if (state.user?.id && state.user.id !== '') {
        return
      }

      // Check if Firebase user exists
      const auth = getAuth()
      const firebaseUser = auth.currentUser

      if (!firebaseUser) {
        // No Firebase user - truly not authenticated, redirect to login
        navigate(fallbackPath, { replace: true, state: { from: location } })
        return
      }

      // Firebase user exists but app state is empty - load user data directly
      if (!isLoadingUser) {
        setIsLoadingUser(true)

        try {
          const user = await getUser(firebaseUser.uid)
          if (user) {
            const email = getEmailForFirebaseUser(firebaseUser)
            setSentryUser({ email, id: firebaseUser.uid })

            // Update app state with loaded user data
            setState({
              ...state,
              user,
              firebaseUser,
              isAuthInitialized: true
            })
          } else {
            // User doesn't exist in Firestore, redirect to login
            navigate(fallbackPath, { replace: true, state: { from: location } })
          }
        } catch (error) {
          navigate(fallbackPath, { replace: true, state: { from: location } })
        } finally {
          setIsLoadingUser(false)
        }
      }
    }

    loadUserData()
  }, [state.isAuthInitialized, state.user, isLoadingUser])

  // Show loading while auth initializes or user data is being loaded
  if (!state.isAuthInitialized || isLoadingUser) {
    return <LoadingPage />
  }

  // If not authenticated, show loading while redirect happens
  if (!state.user?.id || state.user.id === '') {
    return <LoadingPage />
  }

  // If role is required but user doesn't have it, show not authorised page
  if (allowedRoles && allowedRoles.length > 0) {
    const user = state.user

    const activeOrgRole = getActiveOrgRole(user)

    if (!activeOrgRole) {
      return <NotAuthorisedPage />
    }

    const currentRouteMetadata = getRouteMetadata(location.pathname)
    // Checked before the role list: an org ADMIN satisfies `allowedRoles`, and these routes are for
    // platform operators only.
    if (currentRouteMetadata?.adminOnly && !isPlatformSuperAdmin(user)) {
      return <NotAuthorisedPage />
    }

    const rolesForPath =
      currentRouteMetadata?.allowedRoles && currentRouteMetadata.allowedRoles.length > 0
        ? currentRouteMetadata.allowedRoles
        : allowedRoles

    if (!rolesForPath.includes(activeOrgRole)) {
      const redirectPath = redirectUnauthorizedUser(activeOrgRole, location.pathname)
      return routeType ? <Navigate to={redirectPath} replace /> : <NotAuthorisedPage />
    }
  }

  return <>{children}</>
}
